import React, {useState} from 'react';
import {StyleSheet, Modal, ScrollView, Pressable} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {formatCurrency} from '@/Utils/CommonUtils';

interface Segment {
  pricing_id: number;
  level_slug: string | null;
  level_name: string | null;
  format_slug: string | null;
  format_name: string | null;
  coach_tier_code: string | null;
  coach_tier_name: string | null;
  tournament_id: number | null;
  months_used: number;
  price_per_session: string;
  segment_total: string;
  max_segment_total?: string;  // Display price (MRP) for modal
  currency_code: string;
}

interface ViewBreakupModalProps {
  segments: Segment[] | undefined;
  totalPrice?: string;
  currency?: string;
  title: string;
  buttonLabel?: string;
}

const ViewBreakupModal: React.FC<ViewBreakupModalProps> = ({
  segments,
  totalPrice,
  currency = 'INR',
  title,
  buttonLabel = 'View Breakup',
}) => {
  const [modalVisible, setModalVisible] = useState(false);

  if (!segments || segments.length === 0) {
    return null;
  }

  // Calculate total from max_segment_total values to match displayed segment prices
  // This ensures the total matches exactly what's shown in the table
  const displayTotal = segments.reduce((sum, seg) => {
    const segmentPrice = parseFloat(seg.max_segment_total || seg.segment_total || '0');
    return sum + segmentPrice;
  }, 0);

  return (
    <>
      <Pressable onPress={() => setModalVisible(true)}>
        <CCText fontSize={FontSizes[14]} color={CCColors.Primary.Default} fontWeight="semibold">
          {buttonLabel}
        </CCText>
      </Pressable>

      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}>
        <Box
          flex={1}
          justifyContent="center"
          alignItems="center"
          backgroundColor="rgba(0,0,0,0.4)">
          <Box
            width="90%"
            maxWidth={500}
            maxHeight="70%"
            backgroundColor={CCColors.White}
            borderRadius={12}
            padding={16}
            borderWidth={1}
            borderColor={CCColors.Grey.Divider200}>
            <Box
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={12}>
              <CCText
                fontSize={FontSizes[16]}
                fontWeight="bold"
                color={CCColors.TextColor.Dark}>
                {title}
              </CCText>
              <Pressable onPress={() => setModalVisible(false)}>
                <CCText
                  fontSize={FontSizes[14]}
                  color={CCColors.Primary.Brown}
                  fontWeight="semibold">
                  Close
                </CCText>
              </Pressable>
            </Box>
            <ScrollView>
              {/* Table Header */}
              <Box
                flexDirection="row"
                backgroundColor={CCColors.Primary.lightBrown}
                padding={12}
                borderRadius={8}
                marginBottom={8}
                borderBottomWidth={2}
                borderBottomColor={CCColors.Primary.Default}>
                <Box flex={2} style={styles.tableHeaderCell}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}>
                    Level
                  </CCText>
                </Box>
                <Box flex={1.5} style={styles.tableHeaderCell}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}>
                    Sessions
                  </CCText>
                </Box>
                <Box flex={2} style={styles.tableHeaderCell}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}>
                    Format
                  </CCText>
                </Box>
                <Box flex={1.5} style={styles.tableHeaderCell}>
                  <CCText
                    fontSize={FontSizes[13]}
                    fontWeight="bold"
                    color={CCColors.TextColor.Dark}
                    textAlign="right">
                    Price
                  </CCText>
                </Box>
              </Box>

              {/* Table Rows */}
              {segments.map((seg, segIdx) => {
                const sessionsCount = seg.months_used * 8;
                const sessionsText =
                  sessionsCount === 1
                    ? `${sessionsCount} session`
                    : `${sessionsCount} sessions`;

                return (
                  <Box
                    key={`${seg.pricing_id}_${segIdx}`}
                    flexDirection="row"
                    padding={12}
                    borderBottomWidth={1}
                    borderBottomColor={CCColors.Grey.Divider200}
                    alignItems="center">
                    <Box flex={2} style={styles.tableCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        color={CCColors.TextColor.Dark}
                        fontWeight="semibold">
                        {seg.level_name || seg.level_slug || 'N/A'}
                      </CCText>
                    </Box>
                    <Box flex={1.5} style={styles.tableCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        color={CCColors.TextColor.Dark}>
                        {sessionsText}
                      </CCText>
                    </Box>
                    <Box flex={2} style={styles.tableCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        color={CCColors.TextColor.Dark}>
                        {seg.format_name || seg.format_slug || 'N/A'}
                      </CCText>
                    </Box>
                    <Box flex={1.5} style={styles.tableCell}>
                      <CCText
                        fontSize={FontSizes[13]}
                        color={CCColors.TextColor.Dark}
                        textAlign="right"
                        fontWeight="semibold">
                        {formatCurrency(
                          parseFloat(seg.max_segment_total || seg.segment_total || '0'),
                          seg.currency_code || currency || 'INR',
                        )}
                      </CCText>
                    </Box>
                  </Box>
                );
              })}

              {/* Total Price Row */}
              <Box
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                padding={16}
                marginTop={8}
                backgroundColor={CCColors.Primary.lightBrown}
                borderRadius={8}
                borderTopWidth={2}
                borderTopColor={CCColors.Primary.Default}>
                <CCText
                  fontSize={FontSizes[16]}
                  fontWeight="bold"
                  color={CCColors.TextColor.Dark}>
                  Total Price
                </CCText>
                <CCText
                  fontSize={FontSizes[18]}
                  fontWeight="bold"
                  color={CCColors.Primary.Default}>
                  {formatCurrency(displayTotal, currency || 'INR')}
                </CCText>
              </Box>
            </ScrollView>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  tableHeaderCell: {
    paddingRight: 8,
  },
  tableCell: {
    paddingRight: 8,
  },
});

export default ViewBreakupModal;

